import { Delete, Download } from '@mui/icons-material';
import { IconButton, ListItem, ListItemText, Tooltip } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { deleteFile } from '../../../Services/Files';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { ConfirmActionDialog } from '../../Common/ConfirmActionDialog/ConfirmActionDialog';
import { Loader } from '../../Loader/Loader';

interface FileListItemProps {
  file: AttachmentFile;
  preventEdit?: boolean;
}

export const FileListItem = ({ file, preventEdit }: FileListItemProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showConfirmDeleteDialog, setShowConfirmDeleteDialog] = useState(false);
  const [loading, setIsLoading] = useState(false);

  const { mutate: deleteFileMutation } = useMutation({
    mutationFn: () => deleteFile(file.id),
    onError: () => {
      toast.error(t('toast.errors.something_went_wrong'));
      setIsLoading(false);
    },
    onSuccess: () => {
      toast.success(t('toast.success.delete', { elem: 'File' }));
      queryClient.invalidateQueries({ queryKey: ['getCandidateFiles'] });
      queryClient.invalidateQueries({ queryKey: ['getJobFiles'] });
      queryClient.invalidateQueries({ queryKey: ['getCandidateJobFiles'] });
      setIsLoading(false);
    },
  });

  const handleDelete = () => {
    setIsLoading(true);
    deleteFileMutation();
    setShowConfirmDeleteDialog(false);
  };

  return (
    <>
      {<Loader isLoading={loading} />}
      <ListItem>
        <ListItemText primary={file.name} />
        <Tooltip title="Download">
          <IconButton component="a" href={file.url}>
            <Download />
          </IconButton>
        </Tooltip>
        {!preventEdit && (
          <Tooltip title="Delete" sx={{ marginLeft: 3 }}>
            <IconButton onClick={() => setShowConfirmDeleteDialog(true)}>
              <Delete />
            </IconButton>
          </Tooltip>
        )}
      </ListItem>

      <ConfirmActionDialog
        open={showConfirmDeleteDialog}
        handleClose={() => setShowConfirmDeleteDialog(false)}
        handleConfirm={handleDelete}
        description={t('attachments.delete_confirm', { elem: `${file.name}` })}
      />
    </>
  );
};
